import { useContext, useRef, useState } from "react";
import GruposService from "../api/grupos.api";
import VentasService from "../api/ventas.api";
import AuthService from "../../../services/auth.service";
import Roles from "../../../config/roles";
import Toasts from "../../../components/notificacion/Toasts";
import { StateContext } from "../../../components/context/StateProvider";
import { ORDER_STATE_N2 } from "../../../config/constants";
import { getErrorMessage } from "../../../utils/ErrorUtils";

export const ACCION_GRUPO = Object.freeze({
    ELIMINAR: 'eliminar',
    COMPLETAR: 'completar'
});

/**
 * Hook para eliminar un grupo de Nave 2 o forzarlo como completado, con confirmación.
 *
 * - Eliminar: solo grupos PENDIENTE (el backend rechaza el resto) y solo admin, que es
 *   quien tiene `grupos_n2.eliminar`.
 * - Completar: cualquier grupo, admin o almacén (`grupos_n2.actualizar_estado`). Es la
 *   salida para los grupos colgados con punteo, que no se pueden borrar: un grupo
 *   COMPLETADO deja de salir en "Grupos activos".
 *
 * DECISION: al completar, los pedidos del grupo quedan libres y volverían a la lista de
 * pedidos. Por eso, si `forzarPedidos` está marcado, también se fuerzan como preparados
 * (con el flag `forzado`, sin avisar a Nave 1) los que sigan pendientes en esa lista.
 *
 * @param {Array} pedidosN2 - Pedidos cargados en la lista de Nave 2 (num, type, state)
 * @param {Function} getPedidosGrupo - Devuelve los números de pedido de un grupo
 * @param {Function} onTerminado - Se llama tras una acción con éxito, para recargar
 * @returns {{ puedeEliminar: boolean, puedeCompletar: boolean, accion: Object|null,
 *   procesando: boolean, forzarPedidos: boolean, setForzarPedidos: Function,
 *   pedidosPendientes: Array, solicitar: Function, cancelar: Function, confirmar: Function }}
 */
const useAccionesGrupoN2 = (pedidosN2, getPedidosGrupo, onTerminado) => {
    const { state, updateState } = useContext(StateContext);

    const estadoRef = useRef(state);
    estadoRef.current = state;

    const [roles] = useState(() => AuthService.getUserRoles() || []);
    const puedeEliminar = roles.includes(Roles.ADMIN.role);
    const puedeCompletar = puedeEliminar || roles.includes(Roles.ALMACEN.role);

    // { tipo: ACCION_GRUPO, grupo }
    const [accion, setAccion] = useState(null);
    const [procesando, setProcesando] = useState(false);
    const [forzarPedidos, setForzarPedidos] = useState(true);

    const notificar = (mensaje, tipo) => {
        const toast = Toasts.generateToast(mensaje, tipo);
        updateState({ toasts: [...estadoRef.current.toasts, toast] });
    };

    // Pedidos del grupo que siguen pendientes en la lista: los únicos que hay que forzar
    const numerosGrupo = accion ? getPedidosGrupo(accion.grupo) : [];
    const pedidosPendientes = pedidosN2.filter(
        (p) => numerosGrupo.includes(p.num) && p.state !== ORDER_STATE_N2.PREPARADO
    );

    const solicitar = (tipo, grupo) => {
        setForzarPedidos(true);
        setAccion({ tipo, grupo });
    };

    const cancelar = () => setAccion(null);

    const forzarPedidosPendientes = async () => {
        const resultados = await Promise.allSettled(
            pedidosPendientes.map((p) =>
                VentasService.forzarPreparadoN2(p.num, estadoRef.current.year, p.type)
            )
        );
        return resultados.filter((r) => r.status === 'rejected').length;
    };

    const confirmar = async () => {
        if (!accion) return;

        const { tipo, grupo } = accion;
        setProcesando(true);

        try {
            if (tipo === ACCION_GRUPO.ELIMINAR) {
                await GruposService.delete(grupo.id);
                notificar('Grupo eliminado correctamente', 'success');
            } else {
                await GruposService.updateEstado(grupo.id, estadoRef.current.year, 'COMPLETADO');

                const fallidos = forzarPedidos ? await forzarPedidosPendientes() : 0;

                if (fallidos > 0) {
                    notificar(
                        `Grupo completado, pero ${fallidos} ${fallidos === 1 ? 'pedido no se pudo' : 'pedidos no se pudieron'} marcar como preparado`,
                        'warning'
                    );
                } else {
                    notificar('Grupo marcado como completado', 'success');
                }
            }

            setAccion(null);
            onTerminado();
        } catch (error) {
            const porDefecto = tipo === ACCION_GRUPO.ELIMINAR
                ? 'Error al eliminar el grupo'
                : 'Error al completar el grupo';
            notificar(getErrorMessage(error, porDefecto), 'error');
        } finally {
            setProcesando(false);
        }
    };

    return {
        puedeEliminar,
        puedeCompletar,
        accion,
        procesando,
        forzarPedidos,
        setForzarPedidos,
        pedidosPendientes,
        solicitar,
        cancelar,
        confirmar
    };
};

export default useAccionesGrupoN2;
